/* eslint-disable no-param-reassign */
import {
  DATA_BUNDLE_MAX_LENGTH,
  DATA_BUNDLE_STATUS_OPEN,
  DATA_BUNDLE_STATUS_CLOSED,
  DATA_BUNDLE_UPLOAD_URL,
  DATA_BUNDLE_STATUS_QUEUED,
  WATCHER_MODE,
} from '../settings';

type DataBundle = {
  createdAt: number | null;
  closedAt: number | null;
  queuedAt: number | null;
  bytesLength: number;
  blobParts: BlobPart[];
  status: string;
};

const utils = {
  openingBracket: {
    str: '[',
    blobPart: new TextEncoder().encode('['),
    length: 1,
  },
  closingBracket: {
    str: ']',
    blobPart: new TextEncoder().encode(']'),
    length: 1,
  },
  comma: {
    str: ',',
    blobPart: new TextEncoder().encode(','),
    length: 1,
  },
};

class DataBundler {
  dataBundles: DataBundle[];

  constructor() {
    this.dataBundles = [
      DataBundler.buildDataBundle(),
    ];
  }

  static buildDataBundle({
    bytesLength,
    blobPart,
  }: {
    bytesLength?: number;
    blobPart?: Uint8Array;
  } = {}) {
    const blobParts = [utils.openingBracket.blobPart];
    if (blobPart) {
      blobParts.push(blobPart);
    }
    return {
      createdAt: new Date().getTime(),
      closedAt: null,
      queuedAt: null,
      bytesLength: utils.openingBracket.length + (bytesLength ?? 0),
      blobParts,
      status: DATA_BUNDLE_STATUS_OPEN,
    };
  }

  closeDataBundle(dataBundle: DataBundle) {
    if (dataBundle.status === DATA_BUNDLE_STATUS_OPEN) {
      dataBundle.closedAt = new Date().getTime();
      dataBundle.status = DATA_BUNDLE_STATUS_CLOSED;
      dataBundle.blobParts.push(utils.closingBracket.blobPart);
      dataBundle.bytesLength += utils.closingBracket.length;
    }
    return this;
  }

  queueDataBundle(dataBundle: DataBundle) {
    if (dataBundle.status !== DATA_BUNDLE_STATUS_QUEUED) {
      if (dataBundle.status === DATA_BUNDLE_STATUS_OPEN) {
        this.closeDataBundle(dataBundle);
      }
      const blob = new Blob(dataBundle.blobParts, { type: 'text/plain' });
      const beaconSent = navigator.sendBeacon(DATA_BUNDLE_UPLOAD_URL, blob);
      if (beaconSent) {
        if (WATCHER_MODE === 'lean') {
          this.dataBundles = this.dataBundles.filter((db) => db !== dataBundle);
        } else {
          dataBundle.queuedAt = new Date().getTime();
          dataBundle.status = DATA_BUNDLE_STATUS_QUEUED;
        }
      }
    }
    return this;
  }

  getLatestDataBundle() {
    return this.dataBundles[this.dataBundles.length - 1];
  }

  handlePayload(payload: unknown) {
    let currentDataBundle = this.getLatestDataBundle();

    if (!currentDataBundle || currentDataBundle.status !== DATA_BUNDLE_STATUS_OPEN) {
      const freshDataBundle = DataBundler.buildDataBundle();
      this.dataBundles.push(freshDataBundle);
      currentDataBundle = freshDataBundle;
    }

    const str = typeof payload === 'string' ? payload : JSON.stringify(payload);
    const blobPart = new TextEncoder().encode(str);
    const potentialNewDataBundleBytesLength = utils.comma.length
      + currentDataBundle.bytesLength
      + blobPart.length;

    if (potentialNewDataBundleBytesLength > DATA_BUNDLE_MAX_LENGTH) {
      this.closeDataBundle(currentDataBundle);
      this.upload();
      // TODO: what if the bytes.length is already larger than DATA_BUNDLE_MAX_LENGTH
      const nextDataBundle = DataBundler.buildDataBundle({
        bytesLength: blobPart.length,
        blobPart,
      });
      this.dataBundles.push(nextDataBundle);
    } else {
      currentDataBundle.bytesLength = potentialNewDataBundleBytesLength;
      if (currentDataBundle.blobParts[currentDataBundle.blobParts.length - 1]
        !== utils.openingBracket.blobPart) {
        currentDataBundle.blobParts.push(utils.comma.blobPart);
      }
      currentDataBundle.blobParts.push(blobPart);
    }
  }

  upload() {
    this.dataBundles.forEach((dataBundle) => {
      this.queueDataBundle(dataBundle);
    });
  }
}

export default DataBundler;
